import { h, Component, Prop, Event, EventEmitter, Element } from '@stencil/core';

/**
 * A segmented control is a horizontal arrangement of two or more segments, each functioning as a distinct button.
 * It is used as a way to offer multiple selections with a single control.
 * ❗The `pk-segmented-control-item` is a child component of `pk-segmented-control`.
 *
 * @example
 * ```html
 * <pk-segmented-control value="2">
 *   <pk-segmented-control-item value="1">Option 1</pk-segmented-control-item>
 *   <pk-segmented-control-item value="2">Option 2</pk-segmented-control-item>
 * </pk-segmented-control>
 * ```
 *
 * @documentation https://b2c.uefa.design/4a7979wvg/p/9861a5-segmented-control/b/24a952
 * @status in-progress
 *
 * @attr {boolean} active - If `true`, the item will be displayed as active.
 * @attr {string} value - The value of the item.
 *
 * @event pkClick - Callback handler emitted when the segmented control item is clicked.
 *
 * @slot - The default slot for the segmented control item content.
 *
 * @part button - The button element.
 *
 * @cssprop --segmented-control-item--bgColor-hover - The background color of the segmented control item on hover.
 * @cssprop --segmented-control-item--font-size - The font size of the segmented control item.
 * @cssprop --segmented-control-item--text-color - The text color of the segmented control item.
 * @cssprop --segmented-control-item--padding - The padding of the segmented control item.
 */
@Component({
  tag: 'pk-segmented-control-item',
  styleUrl: './scss/pk-segmented-control-item.scss',
  shadow: true,
})
export class PkSegmentedControlItem {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkSegmentedControlItemElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** If `true`, the item will be displayed as active. */
  @Prop({ reflect: true }) active: boolean;

  /** The value of the item. */
  @Prop({ reflect: true }) value: string;

  // Prop lifecycle events
  // =======================

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler emitted when the segmented control item is clicked. */
  @Event() pkClick: EventEmitter<HTMLPkSegmentedControlItemElement>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private readonly handleClick = () => {
    this.pkClick.emit(this.el);
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <button
        class={{ 'pk-segmented-control-item': true, 'active font-semibold': this.active }}
        onClick={this.handleClick}
        aria-pressed={this.active ? 'true' : 'false'}
        part="button"
      >
        <slot />
      </button>
    );
  }
}
